import React, { useState } from 'react';
import { motion } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';

const BookingPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    serviceType: '',
    date: '',
    time: '',
    address: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1); // 1 for booking, 2 for payment
  const [paymentMethod, setPaymentMethod] = useState('');
  const [bookingStatus, setBookingStatus] = useState('');

  const serviceOptions = [
    'General Pest Control',
    'Termite Control',
    'Rodent Removal',
    'Bed Bug Treatment',
    'Mosquito Control'
  ];

  const timeSlots = [
    '09:00 AM - 11:00 AM',
    '11:00 AM - 01:00 PM',
    '02:00 PM - 04:00 PM',
    '04:00 PM - 06:00 PM'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    // Clear error for the field being changed
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required.';
    if (!formData.email.trim()) {
      newErrors.email = 'Email Address is required.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid.';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone Number is required.';
    } else if (!/^\d{10,}$/.test(formData.phone.replace(/\s+/g, ''))) { // Basic numeric validation (at least 10 digits)
      newErrors.phone = 'Phone number must be at least 10 digits and contain only numbers.';
    }
    if (!formData.serviceType) newErrors.serviceType = 'Please select a service type.';
    if (!formData.date) newErrors.date = 'Please select a preferred service date.';
    if (!formData.time) newErrors.time = 'Please select a preferred time slot.';
    if (!formData.address.trim()) newErrors.address = 'Address/Location is required.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log('Booking Form Data:', formData);
      setCurrentStep(2); // Move to payment step
      setBookingStatus(''); // Clear previous booking status
    } else {
      console.log('Form validation failed');
    }
  };

  const handlePaymentSelect = (method) => {
    setPaymentMethod(method);
  };

  const handleConfirmBooking = () => {
    // Placeholder for actual booking confirmation logic
    console.log('Final Booking Details:', { ...formData, paymentMethod });
    if (paymentMethod === 'online') {
      // Simulate API call or redirection
      setTimeout(() => {
        setBookingStatus('Booking confirmed! Payment successful (Simulated).');
        resetForm();
      }, 1500);
    } else if (paymentMethod === 'cash') {
      setBookingStatus('Booking confirmed! Payment will be collected on service.');
      resetForm();
    } else {
        setBookingStatus('Please select a payment method.')
    }
  };
  
  const resetForm = () => {
    setFormData({
        fullName: '',
        email: '',
        phone: '',
        serviceType: '',
        date: '',
        time: '',
        address: '',
        notes: ''
      });
      setErrors({});
      setPaymentMethod('');
      setCurrentStep(1); // Reset to booking form for a new booking after a delay
      // setBookingStatus(''); // Keep status message for a bit
  }

  const pageVariants = {
    initial: { opacity: 0, x: "-100vw" },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: "100vw" }
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.8
  };

  return (
    <motion.div 
      className="container mt-5 mb-5"
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
    >
      {currentStep === 1 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="mb-4 text-center">Book Our Pest Control Services</h1>
          <form onSubmit={handleBookingSubmit} noValidate>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="fullName" className="form-label">Full Name <span className="text-danger">*</span></label>
                <input type="text" className={`form-control ${errors.fullName ? 'is-invalid' : ''}`} id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required />
                {errors.fullName && <div className="invalid-feedback">{errors.fullName}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="email" className="form-label">Email Address <span className="text-danger">*</span></label>
                <input type="email" className={`form-control ${errors.email ? 'is-invalid' : ''}`} id="email" name="email" value={formData.email} onChange={handleChange} required />
                {errors.email && <div className="invalid-feedback">{errors.email}</div>}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="phone" className="form-label">Phone Number <span className="text-danger">*</span></label>
                <input type="tel" className={`form-control ${errors.phone ? 'is-invalid' : ''}`} id="phone" name="phone" value={formData.phone} onChange={handleChange} required pattern="[0-9]*" />
                {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="serviceType" className="form-label">Select Service Type <span className="text-danger">*</span></label>
                <select className={`form-select ${errors.serviceType ? 'is-invalid' : ''}`} id="serviceType" name="serviceType" value={formData.serviceType} onChange={handleChange} required>
                  <option value="">Choose...</option>
                  {serviceOptions.map(service => <option key={service} value={service}>{service}</option>)}
                </select>
                {errors.serviceType && <div className="invalid-feedback">{errors.serviceType}</div>}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="date" className="form-label">Preferred Service Date <span className="text-danger">*</span></label>
                <input type="date" className={`form-control ${errors.date ? 'is-invalid' : ''}`} id="date" name="date" value={formData.date} onChange={handleChange} required min={new Date().toISOString().split("T")[0]} />
                {errors.date && <div className="invalid-feedback">{errors.date}</div>}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="time" className="form-label">Preferred Time Slot <span className="text-danger">*</span></label>
                <select className={`form-select ${errors.time ? 'is-invalid' : ''}`} id="time" name="time" value={formData.time} onChange={handleChange} required>
                  <option value="">Choose...</option>
                  {timeSlots.map(slot => <option key={slot} value={slot}>{slot}</option>)}
                </select>
                {errors.time && <div className="invalid-feedback">{errors.time}</div>}
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="address" className="form-label">Address/Location <span className="text-danger">*</span></label>
              <textarea className={`form-control ${errors.address ? 'is-invalid' : ''}`} id="address" name="address" rows="3" value={formData.address} onChange={handleChange} required></textarea>
              {errors.address && <div className="invalid-feedback">{errors.address}</div>}
            </div>

            <div className="mb-3">
              <label htmlFor="notes" className="form-label">Additional Notes or Message</label>
              <textarea className="form-control" id="notes" name="notes" rows="3" value={formData.notes} onChange={handleChange}></textarea>
            </div>
            
            <motion.button 
              type="submit" 
              className="btn btn-primary w-100 py-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Proceed to Payment
            </motion.button>
          </form>
        </motion.div>
      )}

      {currentStep === 2 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h2 className="mb-4 text-center">How Would You Like to Pay?</h2>
          <div className="d-grid gap-3 mb-4">
            <motion.button 
                className={`btn ${paymentMethod === 'online' ? 'btn-success' : 'btn-outline-success'} p-3`} 
                onClick={() => handlePaymentSelect('online')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                Online Payment
            </motion.button>
            <motion.button 
                className={`btn ${paymentMethod === 'cash' ? 'btn-info' : 'btn-outline-info'} p-3`} 
                onClick={() => handlePaymentSelect('cash')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                Cash on Service
            </motion.button>
          </div>
          
          {paymentMethod && (
            <motion.button 
                className="btn btn-primary w-100 py-2 mt-3" 
                onClick={handleConfirmBooking}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                Confirm Booking
            </motion.button>
          )}
          <button className="btn btn-link mt-3 d-block mx-auto" onClick={() => {setCurrentStep(1); setBookingStatus('');}}>Back to Booking Details</button>
        </motion.div>
      )}
      
      {bookingStatus && (
        <motion.div 
            className={`alert ${bookingStatus.includes('successful') || bookingStatus.includes('confirmed') ? 'alert-success' : 'alert-danger'} mt-4`} 
            role="alert"
            initial={{ opacity: 0, y:10 }}
            animate={{ opacity: 1, y:0 }}
            transition={{delay: 0.2}}
        >
          {bookingStatus}
        </motion.div>
      )}
    </motion.div>
  );
};

export default BookingPage;
